import Dropzone from "dropzone";
import hasNotifications from "@/mixins/has-notifications";
import { getFileExtension } from "@/services/regex";

Dropzone.autoDiscover = false;

const getFileSpec = (dropzone, file) => ({
  fileSize: file.upload.total || file.size,
  fileSizeStr: dropzone.filesize(file.upload.total || file.size),

  bytesSent: file.upload.bytesSent,
  bytesSentStr: dropzone.filesize(file.upload.bytesSent),

  status: file.status
});

const findFile = (dropzone, uuid) =>
  dropzone._vueComponent.files.find(({ id }) => id === uuid);

const messageMatchTemplate = (message, template) => {
  if (message === template) return true;
  const regex = template
    // dropzone template treatment, replace with special intermediate format so as not to be escaped in the next step
    .replace(/{{.+}}/g, "_ANY")
    // escape and convert to regex string, see: https://stackoverflow.com/a/6969486/1999083
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    // replace intermediate format back to final regex pattern
    .replaceAll("_ANY", ".+");

  return message.match(regex) !== null;
};

const defaultDropzoneOptions = (rootClass, dnd) => ({
  clickable: `.${rootClass} .dropzone-clickable`,
  hiddenInputContainer: `.${rootClass}`,
  previewsContainer: false,

  dictFileTooBig: "File is too big ({{filesize}}MB)",

  thumbnailWidth: dnd ? 40 : 120,
  thumbnailHeight: 40,
  thumbnailMethod: dnd ? "crop" : "contain",

  accept(file, done) {
    const fileVM = findFile(this, file.upload.uuid);

    const vc = this._vueComponent;
    if (
      vc.dragNDrop &&
      vc.multiple &&
      !["DONE", "CONFIRMING"].includes(vc.existingFilesConfirmingState) &&
      vc.files.length > 1 &&
      vc.files.every(file => vc.isExistingFile(file) || file === fileVM)
    ) {
      vc.existingFilesConfirmingState = "CONFIRMING";

      const _done = () => {
        vc.existingFilesConfirmingState = "DONE";
        done();
      };
      vc.setAlert({
        title: "Do you want to replace existing files?",
        description: `Would you like to replace all existing files with this file`,
        actionText: "Yes. Clear existing files",
        actionFn: () => {
          vc.removeAllKeeping(fileVM);
          // wait vue done its job before uploading the new file
          vc.$nextTick(_done);
        },
        cancelText: "No. Keep existing files",
        cancelFn: _done
      });
    } else {
      return done();
    }
  },

  // event overrides
  addedfile(file) {
    this._vueComponent.addFile(
      {
        id: file.upload.uuid,
        fileName: file.name,
        ...getFileSpec(this, file),
        fileStatus: "Pending",
        fileType: getFileExtension(file.name),
        thumbnail: null,
        accepted: null,
        uploadId: null
      },
      file
    );
  },
  uploadprogress(file) {
    const fileVM = findFile(this, file.upload.uuid);
    Object.assign(fileVM, getFileSpec(this, file));
  },

  thumbnail(file, dataUrl) {
    const fileVM = findFile(this, file.upload.uuid);
    fileVM.thumbnail = dataUrl;
  },
  error(file, message, xhr) {
    const fileVM = findFile(this, file.upload.uuid);

    fileVM.status = file.status;
    fileVM.accepted = file.accepted;
    if (file.accepted === false) {
      // [Client Side error] There could be 3 possible reasons as to why a file is not accepted: invalid, too large or max file reached. And there is no good way for us to tell other than check the error message

      console.error(`File ${file.name} upload failed`, message);

      if (messageMatchTemplate(message, this.options.dictFileTooBig)) {
        const error = "File too large";
        fileVM.fileStatus = error;
        this._vueComponent.setAlert({
          title: error,
          description: `${message}. Please upload a file smaller than ${this._vueComponent.maxFilesizeStr}.`,
          actionText: "Try Again"
        });
      } else if (
        messageMatchTemplate(message, this.options.dictInvalidFileType)
      ) {
        fileVM.fileStatus = "Invalid File";
        this._vueComponent.setAlert({
          title: "File type not recognized",
          description: `We don't recognize the file you attempted to upload. Please upload a ${this._vueComponent.acceptedFileNames} file.`,
          actionText: "Try Again"
        });
      } else if (
        messageMatchTemplate(message, this.options.dictMaxFilesExceeded)
      ) {
        fileVM.fileStatus = "Too many files";
        this._vueComponent.setAlert({
          title: "File limit reached",
          description: `No more than ${this.options.maxFiles} file(s) is allowed.`,
          actionText: "Remove a file and try again"
        });
      } else {
        fileVM.fileStatus = "Failed to Upload";
      }
    } else if (file.accepted === true) {
      // [Server Side error]
      const errorStatus = "Failed to Upload";
      const errorMessage = xhr.responseText;

      fileVM.fileStatus = errorStatus;
      console.error("Unknown error type.", errorMessage);
      this._vueComponent.setAlert({
        title: errorStatus,
        description: errorMessage,
        actionText: "Please try again"
      });
    }
  },
  success(file, { id }) {
    const fileVM = findFile(this, file.upload.uuid);
    fileVM.status = file.status;
    fileVM.uploadId = id;
  }
});

export default {
  name: "BaseFileUpload",
  mixins: [hasNotifications],
  props: {
    // https://www.dropzonejs.com/#config-acceptedFiles
    acceptedFiles: Object,
    dropzoneOptions: Object,
    disabled: Boolean
  },
  data: () => ({
    maxFilesizeStr: null
  }),
  mounted() {
    this.dropzone = new Dropzone(this.$el, this.resolvedDropzoneOptions);
    this.dropzone._vueComponent = this;

    const { maxFilesize, filesizeBase } = this.dropzone.options;
    if (maxFilesize) {
      // convert MB to KB
      if (maxFilesize < 1)
        this.maxFilesizeStr = `${maxFilesize * filesizeBase}KB`;
      else if (maxFilesize < 1024) this.maxFilesizeStr = `${maxFilesize}MB`;
    }

    this.handleDisabled(this.disabled);
  },
  computed: {
    rootClass() {
      return `file-upload-${this._uid}`;
    },
    resolvedDropzoneOptions() {
      return {
        ...defaultDropzoneOptions(this.rootClass, this.dragNDrop),

        ...(this.acceptedFileTypes && {
          acceptedFiles: this.acceptedFileTypes
        }),

        ...(this.dragNDrop &&
          !this.multiple && {
            maxFiles: 1
          }),

        ...this.dropzoneOptions
      };
    },
    acceptedFileNames() {
      return this.acceptedFiles
        ? Object.keys(this.acceptedFiles)
            .join(", ")
            .replace(/, ([^,]*)$/, ", or $1")
            .replace(/^([^,]*),([^,]*)$/, "$1$2")
        : "";
    },
    acceptedFileTypes() {
      return this.acceptedFiles
        ? Object.values(this.acceptedFiles).join(",")
        : null;
    },
    acceptedFileTypePrefixes() {
      return this.acceptedFiles
        ? Object.values(this.acceptedFiles)
            .map(mimeType => mimeType.replace(/(\w+)\/.+/, "$1"))
            .dedupe()
        : null;
    },
    successFiles() {
      return this.files.filter(({ status }) => status === "success");
    },
    lastValidFile() {
      return this.successFiles.length > 0
        ? this.successFiles[this.successFiles.length - 1]
        : null;
    }
  },
  watch: {
    successFiles(files, oldFiles) {
      // we are checking against oldFiles because this method gets fired even when there is no change - watch is not relible
      if (files.length === oldFiles.length) return;

      let result = null;
      if (this.multiple) {
        result = files.map(({ uploadId }) => uploadId).dedupe();
      } else if (files.length) {
        result = files[files.length - 1].uploadId;
      }
      this.$emit("input", result);
    },
    disabled(isDisabled) {
      this.handleDisabled(isDisabled);
    }
  },
  methods: {
    handleDisabled(isDisabled) {
      isDisabled ? this.dropzone.disable() : this.dropzone.enable();
      // disable dnd for classic mode
      if (!isDisabled && !this.dragNDrop) {
        const { element, events } = this.dropzone.listeners.find(
          ({ element }) => element === this.$el
        );
        for (const eventName in events) {
          element.removeEventListener(eventName, events[eventName], false);
        }
      }
    }
  }
};
